import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Check, Copy, ExternalLink, Mail, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetupCompletePage = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchSetupData = async () => {
      if (!sessionId || !token) {
        setError('Invalid session');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/billing/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.payment_status === 'paid' && response.data.setup) {
          setSetupData(response.data.setup);
        } else {
          setError('Payment not completed');
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load setup data');
      } finally {
        setLoading(false);
      }
    };

    fetchSetupData();
  }, [sessionId, token]);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Setting up your visualizer...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/pricing">
            <Button className="bg-black text-white hover:bg-gray-800">
              Back to Pricing
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Success Header */}
      <section className="pt-16 pb-8 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Check className="w-10 h-10 text-white" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl font-black text-gray-900 mb-3"
          >
            🎉 Setup Complete!
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-600"
          >
            Your AI wrap visualizer is ready. Here's everything you need to get started.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 rounded-full text-sm font-medium mt-4"
          >
            <Sparkles className="w-4 h-4" />
            {setupData?.plan?.charAt(0).toUpperCase() + setupData?.plan?.slice(1)} Plan
          </motion.div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto space-y-6">
          
          {/* Visualizer URL */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">🌐</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Your Visualizer URL</h3>
                <p className="text-sm text-gray-500">Share this link with your customers</p>
              </div>
            </div>
            
            <div className="bg-gray-900 rounded-xl p-4 flex items-center justify-between gap-4">
              <code className="text-green-400 text-sm break-all">
                {setupData?.visualizer_url}
              </code>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-gray-800"
                  onClick={() => copyToClipboard(setupData?.visualizer_url, 'url')}
                >
                  {copiedField === 'url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <a href={setupData?.visualizer_url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="ghost" className="text-white hover:bg-gray-800">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Button Code */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">🔘</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Button Code</h3>
                <p className="text-sm text-gray-500">Add this button to your website</p>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Preview:</p>
              <div dangerouslySetInnerHTML={{ __html: setupData?.button_code }} />
            </div>
            
            <div className="bg-gray-900 rounded-xl p-4 relative">
              <pre className="text-gray-300 text-xs overflow-x-auto whitespace-pre-wrap">
                {setupData?.button_code}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-2 right-2 text-white hover:bg-gray-800"
                onClick={() => copyToClipboard(setupData?.button_code, 'button')}
              >
                {copiedField === 'button' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </motion.div>

          {/* iFrame Code */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">📦</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">iFrame Embed Code</h3>
                <p className="text-sm text-gray-500">Embed the visualizer directly on your website</p>
              </div>
            </div>
            
            <div className="bg-gray-900 rounded-xl p-4 relative">
              <pre className="text-gray-300 text-xs overflow-x-auto whitespace-pre-wrap">
                {setupData?.iframe_code}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-2 right-2 text-white hover:bg-gray-800"
                onClick={() => copyToClipboard(setupData?.iframe_code, 'iframe')}
              >
                {copiedField === 'iframe' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </motion.div>

          {/* Email Confirmation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-blue-50 rounded-2xl p-6 border border-blue-100"
          >
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-blue-600" />
              <p className="text-blue-800">
                <strong>Check your email!</strong> We've sent you a confirmation with all these details.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Start Guide */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
          >
            <h3 className="font-bold text-gray-900 mb-4">📚 Quick Start Guide</h3>
            
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                  1
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Share your URL</h4>
                  <p className="text-sm text-gray-600">
                    Post your visualizer link on social media, add it to your bio, or send it to customers directly.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                  2
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Add the button to your site</h4>
                  <p className="text-sm text-gray-600">
                    Copy the button code and paste it into your website's HTML where you want it to appear.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                  3
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Embed the full visualizer</h4>
                  <p className="text-sm text-gray-600">
                    Use the iframe code to embed the complete visualizer experience directly on your page.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <Link to="/app">
              <Button className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-base">
                Go to Dashboard
              </Button>
            </Link>
            <p className="text-sm text-gray-500 mt-4">
              Manage your wraps, view analytics, and more
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SetupCompletePage;
